import React, {
  useState, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CoreLink, Taxonomy } from '@uncinc/react-drupal-core';
import classnames from 'classnames';

import {
  Accordion, AccordionItem, Button,
} from '@uncinc/uncinc-react-kitchen-sink';

import useDossierIdParam from '../../hooks/useDossierIdUrlParam';
import useMatchingDossierContentParagraph from '../../hooks/useMatchingDossierContentParagraph';
import useScrollPastNavHeight from '../../hooks/useScrollPastNavHeight';
import './style.scss';

const Submenu: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Extract dossier_id from URL parameters using custom hook
  const dossierId = useDossierIdParam();

  // Get Navigation's current position (top) and height (responsive across breakpoints)
  const { top: navigationTop, height: navigationHeight } = useScrollPastNavHeight();
  // Formula: submenuTop = navigationHeight + navigationCurrentTop (min 0)
  const submenuTop = Math.max(0, navigationHeight + navigationTop);

  // Get dossier entity from Redux (Taxonomy component fetches it automatically)
  const dossier = useSelector((state: any) => state.getIn(['taxonomyTerm', dossierId], null));

  // Convert dossier to JS for hook
  const dossierData = dossier?.toJS();

  // Find the dossier_content paragraph containing the current article
  const matchingParagraph = useMatchingDossierContentParagraph(
    dossierData?.field_paragraphs ?? [],
  );

  // Get title from matching paragraph, fallback to dossier name
  const title = matchingParagraph?.entity?.field_title?.[0]?.value
    ?? dossier?.getIn(['name', 0, 'value'], '');
  const dossierLink = dossier?.getIn(['paths', 'nl']);

  const accordionRef = useRef<HTMLDivElement>(null);

  const handleAccordionChange = (collapsed: boolean) => {
    setIsOpen(!collapsed);
  };

  const handleOverlayClick = () => {
    const button = accordionRef.current?.querySelector('button');
    if (button) {
      button.click();
    }
  };

  const Classes = classnames('Submenu', {
    'Submenu--open': isOpen,
    hidden: dossier === null,
  });

  return (
    <>
      {isOpen && (
        <div
          className="Submenu__overlay"
          onClick={handleOverlayClick}
          role="presentation"
        />
      )}

      <div
        className={Classes}
        style={{ top: submenuTop }}
        ref={accordionRef}
      >
        <Accordion>
          <AccordionItem
            collapsed={!isOpen}
            onChange={handleAccordionChange}
            title={(
              <div className="Submenu__header">
                <div className="Submenu__header__content">
                  <div className={`Submenu__header__wrapper ${isOpen ? 'Submenu__header__wrapper--open' : ' '}`}>
                    {isOpen ? (
                      <CoreLink href={dossierLink}>
                        <i className="icon icon-chevron-right icon-variant-0" />
                        {t('dossier.back_to_dossier')}
                      </CoreLink>
                    ) : (
                      <h3 className="Submenu__title">{`${t('general.subject')}: ${title}`}</h3>
                    )}

                  </div>
                  <div className="Submenu__header__button">
                    <Button
                      className="Button Button--ghost"
                      href="#"
                      onClick={(e: any) => {
                        e.preventDefault();
                        handleAccordionChange(true);
                      }}
                    >
                      {!isOpen ? (
                        <span>
                          <span>{t('dossier.submenu.show_content')}</span>
                          <div className="icon icon-chevron-right" />
                        </span>
                      ) : (
                        <span>
                          <span>{t('dossier.submenu.hide_content')}</span>
                          <div className="icon icon-chevron-right" />
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            content={(
              <div className="Submenu__content">
                <div className="Submenu__content__subtitle">
                  {`${t('general.subject')}: ${title}`}
                </div>
                <ul className="Submenu__links">
                  <Taxonomy
                    id={dossierId}
                    showAsMenuItem
                    onClick={() => {
                      handleAccordionChange(false);
                      handleOverlayClick();
                    }}
                  />
                </ul>
              </div>
            )}
          />
        </Accordion>
      </div>
    </>
  );
};

export default Submenu;
